import { useMemo } from "react";
import { MapaBolhas, type PontoMapa } from "@/components/mapa/MapaBolhas";
import { Skeleton } from "@/components/ui/skeleton";
import { useMunicipios } from "@/lib/municipios";
import { useMapaClientes } from "../api";
import { resolverClientes } from "../clientes";
import { CORES, formatInteiro, formatNumero } from "../series";
import { GradeKpis, Kpi, type Variante } from "./Kpi";

const clientes = (n: number) => `${formatInteiro(n)} ${n === 1 ? "cliente" : "clientes"}`;

/** Onde estão os clientes ativos: mapa por município e quantas cidades/estados. */
export function TelaClientes({ variante }: { variante: Variante }) {
  const tv = variante === "tv";
  const mapa = useMapaClientes({ autoAtualizar: tv });
  const municipios = useMunicipios();

  // Código IBGE primeiro; sem ele, o município/UF do cadastro casado por nome (ver clientes.ts).
  const { pontos, estados, semLocalizacao } = useMemo(() => {
    if (!mapa.data || !municipios.data) return { pontos: [], estados: 0, semLocalizacao: 0 };
    const { cidades, semLocalizacao } = resolverClientes(mapa.data, municipios.data);
    const pontos: PontoMapa[] = cidades.map(({ municipio, clientes: n }) => ({
      chave: municipio.codigoIbge,
      lat: municipio.lat,
      lng: municipio.lng,
      valor: n,
      tooltip: (
        <>
          <strong>
            {municipio.nome}/{municipio.uf}
          </strong>
          <br />
          {clientes(n)}
        </>
      ),
    }));
    return { pontos, estados: new Set(cidades.map((c) => c.municipio.uf)).size, semLocalizacao };
  }, [mapa.data, municipios.data]);

  const carregando = mapa.isLoading || municipios.isLoading;
  const pronto = !!mapa.data && !!municipios.data;

  const kpis = (
    <GradeKpis variante={variante} colunas={tv ? 1 : 2}>
      <Kpi rotulo="Cidades com clientes" valor={formatNumero(pronto ? pontos.length : undefined)} isLoading={carregando} variante={variante} cor={CORES.verde} />
      <Kpi rotulo="Estados" valor={formatNumero(pronto ? estados : undefined)} isLoading={carregando} variante={variante} cor={CORES.azul} />
    </GradeKpis>
  );
  const aviso = pronto && semLocalizacao > 0 && (
    <p className={tv ? "text-lg text-slate-500" : "text-xs text-muted-foreground"}>
      {clientes(semLocalizacao)} ativos sem localização (sem CEP válido e município não reconhecido).
    </p>
  );
  const mapaOuEsqueleto = pronto ? (
    // Sem rótulos fixos nem na TV: só as bolhas, como um mapa de calor.
    <MapaBolhas pontos={pontos} cor={CORES.verde} variante={variante} />
  ) : (
    <Skeleton className={tv ? "h-full w-full rounded-xl bg-white/10" : "h-full w-full rounded-xl"} />
  );

  if (tv) {
    return (
      <div className="flex h-full gap-8">
        <div className="relative z-0 min-w-0 flex-1 overflow-hidden rounded-xl">{mapaOuEsqueleto}</div>
        <aside className="flex w-80 flex-col justify-center gap-6">
          {kpis}
          {aviso}
        </aside>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {kpis}
      {/* z-0: os panes do Leaflet têm z-index alto e passariam por cima do menu. */}
      <div className="relative z-0 h-[32rem] overflow-hidden rounded-xl border border-border shadow-card">{mapaOuEsqueleto}</div>
      {aviso}
    </div>
  );
}
